import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FiArrowLeft } from 'react-icons/fi';
import './discount.scss';
import { createDiscount, updateDiscount } from './discountService.ts';
import {
  Discount,
  DiscountPayload,
  DiscountPaymentMethod,
  PAYMENT_METHOD_LABELS,
} from './discountTypes.ts';

const ALL_METHODS: DiscountPaymentMethod[] = [
  'efectivo',
  'transferencia',
  'mercadopago',
];

interface DiscountFormProps {
  discount: Discount | null;
  onClose: (refreshList?: boolean) => void;
}

const DiscountForm: React.FC<DiscountFormProps> = ({ discount, onClose }) => {
  const isEditing = !!discount;

  const [name, setName] = useState(discount?.name ?? '');
  const [description, setDescription] = useState(discount?.description ?? '');
  const [percentage, setPercentage] = useState<string>(
    discount ? String(discount.discountPercentage) : ''
  );
  const [methods, setMethods] = useState<DiscountPaymentMethod[]>(
    discount?.paymentMethods ?? []
  );
  const [startDate, setStartDate] = useState(discount?.startDate ?? '');
  const [endDate, setEndDate] = useState(discount?.endDate ?? '');
  const [isActive, setIsActive] = useState(discount?.isActive ?? true);
  const [saving, setSaving] = useState(false);

  const toggleMethod = (m: DiscountPaymentMethod) => {
    setMethods((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    );
  };

  const selectAll = () => setMethods([...ALL_METHODS]);

  const validate = (): string | null => {
    if (!name.trim()) return 'El nombre es requerido';
    const pct = Number(percentage);
    if (!percentage || isNaN(pct) || pct < 1 || pct > 100)
      return 'El porcentaje debe estar entre 1 y 100';
    if (methods.length === 0) return 'Selecciona al menos un medio de pago';
    if (!startDate) return 'La fecha de inicio es requerida';
    if (!endDate) return 'La fecha de fin es requerida';
    if (endDate <= startDate)
      return 'La fecha de fin debe ser posterior a la de inicio';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    const payload: DiscountPayload = {
      name: name.trim(),
      description: description.trim() || undefined,
      discountPercentage: Number(percentage),
      paymentMethods: methods,
      startDate,
      endDate,
      isActive,
    };

    try {
      setSaving(true);
      if (isEditing && discount) {
        await updateDiscount(discount.id, payload);
        toast.success('Descuento actualizado');
      } else {
        await createDiscount(payload);
        toast.success('Descuento creado');
      }
      onClose(true);
    } catch (err: any) {
      toast.error(err.message || 'Error al guardar descuento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="discount-section">
      <button
        type="button"
        className="discount-back-btn"
        onClick={() => onClose()}
      >
        <FiArrowLeft size={18} /> Volver a descuentos
      </button>

      <h4 className="form-section-title">
        {isEditing ? `Editar "${discount!.name}"` : 'Nuevo descuento'}
      </h4>

      <form onSubmit={handleSubmit} className="discount-form">
        <section className="form-card">
          <div className="form-grid-two">
            <div className="form-field">
              <label className="label-product-form">Nombre</label>
              <input
                className="input"
                placeholder="Ej: Promo Abril 2026"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="form-field">
              <label className="label-product-form">
                Porcentaje de descuento
              </label>
              <div className="discount-pct-input-wrapper">
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={100}
                  placeholder="10"
                  value={percentage}
                  onChange={(e) => setPercentage(e.target.value)}
                  required
                />
                <span className="discount-pct-suffix">%</span>
              </div>
            </div>

            <div className="form-field form-field-full">
              <label className="label-product-form">
                Descripción (opcional)
              </label>
              <textarea
                className="textarea"
                placeholder="Descripción del descuento..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        </section>

        <section className="form-card">
          <h5 className="form-section-title">Medios de pago</h5>
          <div className="discount-methods">
            {ALL_METHODS.map((m) => (
              <label key={m} className="discount-method-checkbox">
                <input
                  type="checkbox"
                  checked={methods.includes(m)}
                  onChange={() => toggleMethod(m)}
                />
                <span>{PAYMENT_METHOD_LABELS[m]}</span>
              </label>
            ))}
            <button
              type="button"
              className="discount-select-all-btn"
              onClick={selectAll}
            >
              Todos los medios
            </button>
          </div>
        </section>

        <section className="form-card">
          <h5 className="form-section-title">Período de vigencia</h5>
          <div className="form-grid-two">
            <div className="form-field">
              <label className="label-product-form">Fecha inicio</label>
              <input
                className="input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="form-field">
              <label className="label-product-form">Fecha fin</label>
              <input
                className="input"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
          </div>
        </section>

        <section className="form-card">
          <div className="form-field stock-field">
            <label className="label-product-form">Activo</label>
            <label className="stock-switch">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span className="slider" />
            </label>
          </div>
        </section>

        <div className="form-actions">
          <button type="submit" className="primary-button" disabled={saving}>
            {saving
              ? 'Guardando...'
              : isEditing
                ? 'Actualizar descuento'
                : 'Crear descuento'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DiscountForm;
